import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Button,
  PageHeader,
  Tag,
  Result,
  Spin,
  Typography,
  Alert,
} from 'antd';
import {
  ClockCircleOutlined,
  UnorderedListOutlined,
  UserOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { handleApiError, MyBreadcrumb, accessControls } from 'utils';
import { getProjectColor } from 'utils/time_tracker_project_color';
import { timeTrackerProjectAnalyticsService } from 'services/time_tracker';

const { Text } = Typography;

function formatHours(seconds) {
  if (seconds == null) return '0h';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDuration(seconds) {
  if (seconds == null) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0)
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function TimeTrackerProjectView() {
  const { id } = useParams();
  const history = useHistory();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  useEffect(() => {
    if (permissions == null || !id) return;
    if (!accessControls.can(permissions, 'TimeTracker.read:any')) return;
    setLoading(true);
    timeTrackerProjectAnalyticsService(id)
      .then((res) => {
        setLoading(false);
        setData(res.data);
      })
      .catch((err) => {
        setLoading(false);
        handleApiError(err);
      });
  }, [id, permissions]);

  if (permissions == null) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!accessControls.can(permissions, 'TimeTracker.read:any')) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view this project."
          extra={
            <Button
              type="primary"
              onClick={() => history.push('/time-tracker/projects')}
            >
              Back to Projects
            </Button>
          }
        />
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const { project, analytics, entries } = data;
  if (!project) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="404"
          title="Project not found"
          extra={
            <Button onClick={() => history.push('/time-tracker/projects')}>
              Back to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const a = analytics || {};
  const byUserColumns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (u) =>
        u?.display_name ||
        [u?.first_name, u?.last_name].filter(Boolean).join(' ') ||
        u?.email ||
        '—',
    },
    {
      title: 'Total hours',
      dataIndex: 'totalSeconds',
      key: 'totalSeconds',
      align: 'right',
      render: (s) => formatHours(s),
    },
    {
      title: 'Tasks',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
    },
    {
      title: '% of total',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      render: (p) => `${p}%`,
    },
  ];

  const entryColumns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (u) => u?.display_name || u?.first_name || u?.email || '—',
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (t) => (t ? new Date(t).toLocaleString() : '—'),
    },
    {
      title: 'Duration',
      dataIndex: 'duration_seconds',
      key: 'duration_seconds',
      render: (s) => formatDuration(s),
    },
    {
      title: 'Task',
      key: 'task',
      ellipsis: true,
      render: (_, r) => r.task_title || r.task_description || '—',
    },
  ];

  const locationLabel = (() => {
    const cn = project.company?.company_name || '';
    const bn = project.branch?.branch_name || '';
    const parts = [cn, bn].filter(Boolean);
    return parts.length ? parts.join(' / ') : '';
  })();

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <MyBreadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Time Tracker', path: '/time-tracker/tracker' },
          { label: 'Projects', path: '/time-tracker/projects' },
          { label: project.project_name || 'Detail', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        onBack={() => history.push('/time-tracker/projects')}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                flexShrink: 0,
                background: getProjectColor(project),
              }}
            />
            <span>{project.project_name}</span>
          </span>
        }
        subTitle={locationLabel ? `Company / Branch: ${locationLabel}` : ''}
        extra={
          accessControls.can(permissions, 'TimeTrackerProject.update:any')
            ? [
                <Button
                  key="edit"
                  type="primary"
                  onClick={() =>
                    history.push('/time-tracker/projects/update/' + id)
                  }
                >
                  Edit Project
                </Button>,
              ]
            : []
        }
      />

      <Card size="small" style={{ marginBottom: 24 }} title="Who can access this project">
        {project.assigned_groups && project.assigned_groups.length > 0 ? (
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Access groups (members below may log time once this gate is present):
            </Text>
            {project.assigned_groups.map((g) => (
              <Tag key={g._id}>{g.group_name || g._id}</Tag>
            ))}
            <div style={{ marginTop: 10 }}>
              <Text type="secondary">
                Anyone in those groups may access this project. Anyone on <strong>Team</strong>{' '}
                (below) or the <strong>project creator</strong> may access it too.{' '}
                <strong>Super Admin</strong> (and comparable privileged roles) see every project and
                may be placed on any team.
              </Text>
            </div>
          </div>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="No access groups yet"
            description="This project stays hidden from typical users until at least one user group is assigned on Edit project. Super Admin and similar privileged roles can still manage it. After groups are set, matching group members, users on Team, or the creator can access it. Super Admin may be on any team."
          />
        )}
      </Card>

      {project.project_description && (
        <Card size="small" style={{ marginBottom: 24 }}>
          <Text type="secondary">{project.project_description}</Text>
          {project.team?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">Team: </Text>
              {project.team.map((u) => (
                <Tag key={u._id}>
                  {u.display_name || u.first_name || u.email}
                </Tag>
              ))}
            </div>
          )}
          <div style={{ marginTop: 4 }}>
            <Tag color={project.status === 'active' ? 'green' : 'default'}>
              {project.status || 'active'}
            </Tag>
          </div>
        </Card>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total hours"
              value={a.totalHours ?? 0}
              prefix={<ClockCircleOutlined />}
              suffix="h"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total tasks"
              value={a.totalEntries ?? 0}
              prefix={<UnorderedListOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg per task"
              value={a.avgHoursPerEntry ?? 0}
              prefix={<BarChartOutlined />}
              suffix="h"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Contributors"
              value={a.byUser?.length ?? 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card title="By user" style={{ marginBottom: 24 }}>
        <Table
          rowKey={(r) => r.user?._id || r.user || Math.random()}
          dataSource={a.byUser || []}
          columns={byUserColumns}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No time logged yet' }}
        />
      </Card>

      <Card title="Recent time entries">
        <Table
          rowKey="_id"
          dataSource={entries || []}
          columns={entryColumns}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (t) => `Total ${t} entries`,
          }}
          size="small"
          locale={{ emptyText: 'No entries yet' }}
        />
      </Card>
    </div>
  );
}
